import React from 'react';
import { Say } from '../widgets/Say';
import { SupportedConsonants } from './SupportedConsonants';
import { Say2 } from '../widgets/Say2';
import { SupportedVowels } from './SupportedVowels';
import { SynthNasalFormants } from '../chart/SynthNasalFormants';
import { SynthDiphthongFormants } from '../chart/SynthDiphthongFormants';
import { diphthongFormants, rhoticFormants, syllabicFormants } from '../../phonemes/formants';
import { Linguist } from '../widgets/Linguist';
import { PSlash, PSquare } from '../widgets/Delimit';

export function SpeechEssay() {
  return (<div className='page'>
    <h1>Picking Speech Phonemes</h1>

    <p>
      <Say>/ˈfoʊ̯nim ˌʃɪftɝ/</Say> uses IPA,
      the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>,
      to describe <i>phonemes</i>, or language sounds, to be spoken.
      When you see <Say say="/ə ˈtɹaɪ̯ˌæŋɡəɫ ˈfɑɫɤ̞o̯d baɪ̯ ˈəndɝˌɫaɪ̯nd ˈtɛkst/">a triangle followed by underlined text</Say>,
      you can click on it to hear it spoken.
      See <a href="https://www.internationalphoneticassociation.org/IPAcharts/inter_chart_2018/IPA_2018.html">IPA charts</a> for
      an interactive IPA chart where you can hear the sounds spoken by several linguists.
    </p>

    <p>
      The <Say say="/ˈspit͡ʃ ˈsɪ2nθɐ3ˌsaɪ̯zɝ/">speech synthesizer</Say> uses <a href="https://espeak.sourceforge.net/">eSpeak</a> (wrapped
      by <a href="https://www.masswerk.at/mespeak">meSpeak.js</a>) to produce speech,
      which uses <a href="https://en.wikipedia.org/wiki/Speech_synthesis#Formant_synthesis">formant synthesis</a> to create sounds.
      While not as high quality as many modern synthesizers,
      it's small enough that it runs entirely in the browser without needing a server, and is much more flexible.
      Its flexibility allows <Say>/ˈfoʊ̯nim ˌʃɪftɝ/</Say> to support over 80 consonants (including various affricates and clicks),
      consonant modifiers such as palatalization and aspiration,
      over 60 vowels (including nasals, rhotics, some built-in diphthongs, and syllabic consonants),
      several levels of stress, common combinations of tone indicators, and the ability to set a range of vowel lengths.
      It can approximate many other diphthongs and does its best to approximate other sounds it doesn't support directly.
      This may sound like a lot, but it's just a fraction of the sounds needed to accurately reproduce all the languages of the world.
    </p>

    <p>
      This guide describes the IPA it supports directly and how it approximates other sounds, such as unsupported diphthongs.
      It should help you find sounds that are closest to what your language or accent needs,
      allowing you to customize your choice of phonemes.
      It covers <a href="#consonants">consonants</a>, <a href="#vowels">vowels</a>,&nbsp;
      <a href="#diphthongs-rhotics">diphthongs and rhotics</a>, <a href="#syllabics">syllabic consonants</a>,&nbsp;
      <a href="#length">vowel length</a>, and <a href="#stress">stress, tone, pauses</a>.
    </p>


    <h2 id="consonants">Consonants</h2>

    <p>
      The symbols highlighted in green in the following chart have corresponding sounds in the speech synthesizer.
      The non-green symbols are unsupported, so they're approximated with the nearest supported sound.
      Click on any symbol to hear how the speech synthesizer plays it.
      Below the chart, it will show which sound(s) it actually used.
    </p>

    <SupportedConsonants />

    <p>
      Other supported consonants and approximations:
    </p>

    <ul>
      <li><b>Long:</b> You can make vowels or consonants longer by adding ː, e.g., <Say>/ˈmama/</Say> versus <Say>/ˈmaːmaː/</Say> versus <Say>/ˈmːamːa/</Say>.
        See <a href="#length">vowel length</a> for more options.</li>
      <li><b>Affricates:</b> It supports the lateral affricates <Say2>t͡ɬ</Say2> and <Say2>d͡ɮ</Say2> (not included in the chart above).
        If an affricate isn't built-in, it simply says both consonants, e.g., the <PSlash>p͡f</PSlash> in <Say say="/ˈp͡faɪ̯fɝ/">Pfeiffer /ˈp͡faɪ̯fɝ/</Say>.
        Note that a tie bar is required to make it clear to the speech synthesizer that you want
        an affricate (or co-articulated consonant like <Say2>ŋ͡m</Say2>) rather than separate consonants;
        consider <Say>[ˈnɐtʃɛɫ]</Say> versus <Say>[ˈnɐt͡ʃɛɫ]</Say>, for example.
      </li>
      <li><b>Ejectives:</b> If an ejective isn't built-in, it approximates it by adding a glottal stop <Say2>ʔ</Say2>,
        e.g., <PSlash>qʼa</PSlash> is spoken as <Say say='[ˈqʔa]'>[qʔa]</Say></li>
      <li><b>Palatalized:</b> It supports <PSlash>ʲ</PSlash> to indicate palatalization in some cases,
        such as before a vowel - e.g., <Say>/ˈtʲa/</Say> versus <Say>/ˈta/</Say> - or after some consonants - e.g., <Say>/ˈt͡sarʲ/</Say> versus <Say>/ˈt͡sar/</Say></li>
      <li><b>Velarized:</b> It supports <Say say="/ˈɛɫ/">ɫ</Say>, velarized or "dark l" (alternately <Say say="/ˈɛɫ/">lˠ</Say>).</li>
      <li><b>Aspiration:</b> A few consonants support explicit aspiration, such as <Say2>kʰ</Say2>, <Say2>pʰ</Say2>, <Say2>tʰ</Say2>, and <Say2>t͡sʰ</Say2>.
        Others are followed by a short /h/, e.g., <Say2>qʰ</Say2> versus <Say2>q</Say2> or <Say2>k͡ǁʰ</Say2> versus <Say2>k͡ǁ</Say2></li>
      <li><b>Tenuis:</b> It supports the <i>tenuis</i> consonants <Say2>k˭</Say2> and <Say2>p˭</Say2>,
        meaning that they're voiceless and unaspirated with a VOT
        (voice onset time - the time before the start of the following voiced sound) of close to zero.</li>
      <li><b>Implosives:</b> Implosives aren't supported, so it simply substitutes the corresponding plosive,
        e.g., <PSlash>ɓ</PSlash> is spoken as <Say2>b</Say2></li>
      <li><Say2>ɥ</Say2> a voiced labial-palatal approximant, for example French <Say say="[ɥit]">huit</Say> or Mandarin <Say>[ɥɛn˨˦]</Say></li>
      <li><Say2>ᴅ</Say2> a voiced alveolar flap similar to <Say2>ɾ</Say2>, used for General American, e.g., <Say say="/ˈbɐᴅɝ/">butter [ˈbɐᴅɝ]</Say></li>
      <li><Say2>ɧ</Say2> a voiceless palatal-velar fricative used in Swedish, e.g., <Say say="/ˈɧɵtiʊ̯ˈɧu:/">77 [ˈɧɵtiʊ̯ˈɧu:]</Say></li>
    </ul>


    <h2 id="vowels">Vowels</h2>

    <p>
      Languages and accents get a lot of their personality from the idiosyncrasies of vowels.
      There's an endless variety of vowels because they vary continuously across multiple dimensions:
      open/closed, front/back, (un)rounded, nasal, rhotic, diphthongs, and so on.
    </p>

    <p>
      When a linguist uses a symbol like <Say2>ɑ</Say2>,
      they typically mean a range of possible sounds rather than a specific sound
      because vowels also vary by accent, speaker, and context.
      Even when they narrow it down by adding diacritics such as <Say2>ɑ̈</Say2> (centralized) or <Say2>ɑ̝</Say2> (raised),
      they're still referring to a range of possible sounds.
    </p>

    <p>
      However, a speech synthesizer needs to turn any given symbol into a specific sound, so I needed to pick.
      I used a few criteria for assigning a sound to a symbol, including trying to match the recordings of linguists
      from <a href="https://www.internationalphoneticassociation.org/IPAcharts/inter_chart_2018/IPA_2018.html">IPA charts</a>,
      trying to support some common languages, leveraging choices that eSpeak made, and trying to cover the space of possible sounds.
      However, even among linguists there isn't always agreement on what sound a symbol represents.
      For example, listen to the four recordings of <PSlash>ɞ</PSlash> by different linguists:
      <Linguist phoneme='ɞ' linguist='JE' />, <Linguist phoneme='ɞ' linguist='JH' />, <Linguist phoneme='ɞ' linguist='PL' />,
      and <Linguist phoneme='ɞ' linguist='JW' />.
      Those all sound very different, so I picked one that seemed reasonably close to them while still being distinct: <Say2>ɞ</Say2>.
    </p>

    <p>
      The two charts below arrange vowels in two of the standard ways of presenting vowels:
      1) arranged by vowel quality and 2) graphed by the first two <a href="https://en.wikipedia.org/wiki/Formant">formants</a>.
      In the first chart, when vowels are grouped in pairs, the first is spoken with the mouth unrounded and the second rounded.
      In the second chart, formant F1 increases as you go down (ranging from roughly 250 to 900)
      while formant F2 increases going to the left (ranging from roughly 740 to 2300).
      And again, since vowels vary by accent, speaker, and context, the location of a particular vowel
      symbol on the formant chart isn't really as precise as the chart makes it look.
      Also note that there are higher formants present in the sounds that aren't visualized.
    </p>

    <SupportedVowels />

    <p>
      Nasal vowels are typically specified by adding &nbsp; ̃  &nbsp; to the vowel,
      e.g., the nasal version of <Say2>ɛ</Say2> is <Say2>ɛ̃</Say2>.
      You may also see ⁿ used to indicate a nasalized sound, e.g., <Say2>ɛⁿ</Say2>.
      Here are the supported nasals overlaid on top of the other vowels:
    </p>

    <SynthNasalFormants onlyIpa={true} width={400} height={220} />

    <p>
      Epithentic schwa, represented by <PSlash>ᵊ</PSlash> is spoken as a short schwa, e.g., <Say>/dᵊˈni/</Say>
    </p>


    <h2 id="diphthongs-rhotics">Diphthongs and rhotics</h2>

    <p>
      Diphthongs slide from one vowel to another, e.g., <Say2>aɪ̯</Say2> or <Say2>eʊ̯</Say2>.
      The second vowel has a symbol below it to indicate it's <i>non-syllabic</i>;
      in other words, the two vowels together form the nucleus of one syllable.
      Contrast this with <Say say="/aɪ/">aɪ</Say> or <Say say="/eʊ/">eʊ</Say> where each vowel is a separate syllable.
    </p>

    <p>
      The green lines on the following chart show the built-in diphthongs sliding from one vowel to another.
      Note that assigning a label like <PSlash>aɪ̯</PSlash> doesn't mean that it starts precisely
      at <PSlash>a</PSlash> and ends precisely at <PSlash>ɪ</PSlash>;
      it just means that it starts and ends in the general vicinity of those vowels and those were the closest symbols.
    </p>

    <SynthDiphthongFormants formants={diphthongFormants} />

    <p>
      <i>Rhotic</i> (or r-colored) vowels are rare across languages,
      but they appear in both North American English and Mandarin, each of which have a large number of speakers.
      Rhotics include <Say2>ɝ</Say2>, which appeared on the vowel chart above near its non-rhotic counterpart <Say2>ɜ</Say2>.
      They also include diphthongs that travel from a vowel to the vicinity of <Say2>ɝ</Say2> such as <Say say="/ˈɪːɹ/">ɪɹ</Say> or <Say say="/ˈoːɹ/">oɹ</Say>.
      Rhotics can also be represented by following a vowel with the rhotic-hook character,
      e.g., it interprets <Say say="/ˈa˞/" speed={100}>a˞</Say> as <Say say="/ˈaɹ/" speed={100}>aɹ</Say>.
      The following chart shows the built-in rhotic diphthongs:
    </p>

    <SynthDiphthongFormants formants={rhoticFormants} hideSymbols={['ɞ']} />

    <p>
      The speech synthesizer I'm using is designed for customizing a single language at a time,
      so it only lets you pick a fairly small number of sounds for a single set of symbols.
      Of course, even if you restricted your list of diphthongs so they could only travel
      from any of the 50 supported vowels to any other supported vowel,
      that would still mean 2500 possible diphthongs, way beyond the limit.
      That's why there's a relatively small number of built-in diphthongs.
    </p>

    <p>
      Fortunately, the speech synthesizer can do a reasonable job of transitioning from one vowel
      to another when the second one is marked as extra-short.
      For example, <Say>/ɑe̯/</Say> = <PSquare>ɑĕ</PSquare>, <Say>/ɤʊ̯/</Say> = <PSquare>ɤʊ̆</PSquare>,
      &nbsp;<Say>/æʌ̯/</Say> = <PSquare>æʌ̆</PSquare>, and <Say>/eɹ/</Say> = <PSquare>eɝ̆</PSquare>.
      This offers a reasonable approximation in many cases.
    </p>


    <h2 id="syllabics">Syllabic consonants</h2>

    <p>
      A <i>syllabic consonant</i> is a consonant that serves as the nucleus of a syllable instead of a vowel,
      indicated by adding a line below the consonant.
      Examples in some accents of English include <Say say="[ˈbæfɫ̩]">baffle [ˈbæfɫ̩]</Say>
      , <Say say="[ˈmæksm̩m̩]">maximum [ˈmæksm̩m̩]</Say>, <Say say="[ˈdɪstn̩s]">distance [ˈdɪstn̩s]</Say>, and <Say say="[ˈps̩t]">psst [ˈps̩t]</Say>.
    </p>

    <p>
      The built-in syllabic consonants are <Say2>l̩</Say2>, <Say2>ɫ̩</Say2>, <Say2>m̩</Say2>, <Say2>n̩</Say2>, <Say2>ŋ̩</Say2>, <Say2>r̩</Say2>,
      and <Say2>ɹ̩</Say2> (which uses the same sound as <Say2>ɝ</Say2>).
      The l with the tilde through it, <Say2>ɫ̩</Say2>, is called "dark l", which is a velarized <Say2>l̩</Say2>.
      Because syllabic consonants are similar to vowels, they can also be placed on a formant chart.
      Note that the higher formants, not shown on the chart, differ from the vowels that are close by.
    </p>

    <SynthDiphthongFormants formants={syllabicFormants} hideSymbols={['ɝ']} />

    <p>
      Consonants that sound reasonable as a syllabic include
      some fricatives - such as <Say>[ˈps̩t]</Say>, <Say>[ˈbz̩t]</Say>, or <Say>[ˈbʒ̩ːd]</Say> -
      nasals - such as <Say>[ˈbɐʔn̩]</Say> -
      some approximants - such as <Say>[ˈbɹ̩d]</Say> or <Say>[ˈbɫ̩ːd]</Say> -
      or trills - such as <Say>[ˈɡr̩ː]</Say>.
    </p>


    <h2 id="length">Vowel length</h2>

    <p>
      IPA includes three symbols for indicating vowel length that can be added to a vowel such
      as <Say>/a/</Say> - extra-short <Say>/ă/</Say>, half-long <Say>/aˑ/</Say>, and long <Say>/aː/</Say>.
      Note that this indicates <i>relative</i> length, not absolute.
      A typical context is when talking about a language that differentiates words based on vowel length,
      so perhaps <Say>/ˈbøt/</Say> and <Say>/ˈbøːt/</Say> mean something different.
      Since it's extremely rare for a language to differentiate between more than two or three lengths,
      IPA doesn't need many options for specifying vowel length.
    </p>

    <p>
      However, vowel lengths vary quite a lot across languages, dialects, and context,
      from very short vowels less than 100ms to medium around 150ms or 220ms to longer ones at 300ms or even 400ms.
      If you want a speech synthesizer to be a reasonable approximation of actual speech,
      you need to be able to adjust vowel lengths to match the language.
    </p>

    <p>
      For example, consider the word <i>synthesizer</i>, transcribed as <Say say="[ˈsɪ100nθɐ130ˌsaɪ̯260zɝ220]">/ˈsɪnθɐˌsaɪ̯zɝ/</Say> in a General American accent.
      The synthesizer used a length of 100ms for <Say say="[ɪ100]">ɪ</Say>, 130ms for <Say say="/ɐ/">ɐ</Say>,
      260ms for <Say say="/aɪ̯/">aɪ̯</Say>, and 220ms for <Say say="/ɝ/">ɝ</Say>.
      Trying completely different vowel lengths like this - <Say say="[ˈsɪ200nθɐ300ˌsaɪ̯120zɝ400]">[ˈsɪnθɐˌsaɪ̯zɝ]</Say> -
      doesn't sound like the spoken form and doesn't flow well in a sentence.
      And, in some languages, that might even change the meaning.
    </p>

    <p>
      <Say>/ˈfoʊ̯nim/</Say> let's you set the lengths of vowels (<Say2>a</Say2>), diphthongs (<Say2>aɪ̯</Say2>),
      rhotic diphthongs (<Say2>aɹ</Say2>), and syllabic consonants (<Say2>n̩</Say2>).
      which works even if the system is approximating the diphthong behind the scenes.
      You can also set the length of an unadorned vowel (<Say>/a/</Say>) versus one with a length symbol (<Say>/aː/</Say>),
      useful if your language differentiates vowels by length and you need lengths different from the defaults.
    </p>

    <p>
      It supports explicit lengths in the range 50ms to 580ms.
      The actual lengths may end up being different based on a number of factors,
      so it's most useful to think of these as <i>relative</i> values,
      e.g., a vowel of length 250 will be longer than one of length 150.
      Single vowels default to 160ms, while diphthongs are typically around 220ms by default.
      Extra-short <Say>/ă/</Say> defaults to 50ms, half-long <Say>/aˑ/</Say> defaults to 100ms,
      and the lengthener symbol <Say>/aː/</Say> adds 100ms to the default length.
    </p>


    <h2 id="stress">Stress, tone, pauses</h2>

    <p>
      There's a lot of additional subtlety in how people speak, including how words and syllables are spoken relative to each other.&nbsp;
      <Say>/ˈfoʊ̯nim/</Say> supports stress, tone, and pauses as different ways to control this.
    </p>

    <p>
      Note that a synthesized sentence can sound much more natural if at least some of the words
      in the sentence have stress or tone, though they don't all need to.
      For example, compare <Say>[ðɛɹ ɑɹ ˈmɐɫtɨpɫ̩ ˈwɝdz ɪn ðɪs ˈsɛʔn̩s]</Say>, with a few stress markers,
      versus <Say>[ðɛɹ ɑɹ mɐɫtɨpɫ̩ wɝdz ɪn ðɪs sɛʔn̩s]</Say>, with no stress markers,
      which sounds much less natural.
    </p>

    <p>
      <b>Stress:</b> You can use ˈ for primary stress and ˌ for secondary stress
      by adding them before the syllable to be stressed.
      For example, in English, the word secondary is transcribed as <Say>/ˈsɛkənˌdɛɹi/</Say>,
      where the first syllable gets the most stress, the third syllable gets slight stress, and the other syllables are deemphasized.
      You can also add extra stress by doubling the primary stress indicator,
      e.g., compare <Say>/ˈwət du ˈju wɑntə ˈseɪ̯/</Say> versus <Say>/ˈwət du ˈˈju wɑntə ˈseɪ̯/</Say>.
    </p>

    <p>
      In addition to informing the speech synthesizer how to speak the word,
      stress can be useful when applying rules that determine how vowels change.
      For instance, in Western US, <Say2>ə</Say2> typically changes to <Say2>ɐ</Say2> in a stressed syllable,
      but <Say2>ɨ</Say2> in an unstressed syllable.
      Consider bunny /ˈbəni/, pronounced as <Say>[ˈbɐni]</Say>, versus focus <PSlash>ˈfoʊ̯kəs</PSlash>, pronounced as <Say>[ˈfɤ̞o̯kɨs]</Say>.
    </p>

    <p>
      <b>Tone:</b> You can specify a tone or tones after a vowel, diphthong, or syllable.
      IPA allows you to use one, two, or three tone symbols together to describe how the tone changes across a syllable.
      The following tones and combinations are directly supported, with other combos using the closest match.
    </p>

    <ul>
      <li>basic tones: 1 <Say>/do˩/</Say>, 2 <Say>/ɹe˨/</Say>, 3 <Say>/mi˧/</Say>, 4 <Say>/fɑ˦/</Say>, 5 <Say>/so˥/</Say></li>
      <li>rising: 24 <Say>/wʌ˨˦/</Say>, 15 <Say>/wʌ˩˥/</Say></li>
      <li>falling: 42 <Say>/wʌ˦˨/</Say>, 51 <Say>/wʌ˥˩/</Say></li>
      <li>rising/falling (242) <Say>/oː˨˦˨/</Say>, falling/rising (424) <Say>/oː˦˨˦/</Say></li>
    </ul>

    <p>
      <b>Pause:</b> You can specify a short pause using | and a long pause using ‖.
      If you're not using tones, ‖ causes the final word to drop in intonation,
      while a question mark, ?, will cause it to raise the final word similar to a yes-no question in English.
      For example, <Say>/ˈhaɪ̯ | ˈdʒoʊ̯ ‖ ˈhaʊ̯ ɑɹ ˈju ?/</Say>.
      Roughly, you can think of | as a comma, the non-standard |ː as a semi-colon, and ‖ as a period.
    </p>

    <p>
      IPA may also contain syllable breaks (using a period) or morpheme breaks (using a dash),
      as in <Say>/ˈsɪ.ɫə.bəɫ/</Say> or <Say>/ɹi-ɪˈstæbɫɪʃ/</Say>.
      The speech synthesizer ignores them, though phoneme changing rules may use them to help identify syllables.
    </p>

  </div>);
}
